import React, { useState, useEffect } from 'react';
import "../Shared/Modal.css"
import LoadingPanel from '../Shared/LoadingPanel';
import Form from '../Shared/CustomForm/Form';
import api from '../FetchComponents/FetchComponentsServices';
import { TextInput, SelectInput, CheckboxInput } from '../Shared/CustomForm/Input';

const AdminModal = ({ admin, userNameExists, isOwnUserName, onClose, fetchAdmins }) => {
    const [formData, setFormData] = useState({
        userName: admin.userName ?? '',
        firstName: admin.firstName ?? '',
        lastName: admin.lastName ?? '',
        role: admin.role ?? 'Admin',
        isActive: admin.isActive ?? true,
    });
    const [isLoading, setIsLoading] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({}); //field validation error messages
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const isEditMode = !!admin.id;
    const allowedRoles = ['Admin', 'SuperAdmin'];

    const containsXssPayload = (value) => {
        if (!value) {
            return false;
        }

        const xssPattern = /<[^>]+>|javascript:|on\w+\s*=|&lt;|&gt;/i;
        return xssPattern.test(value);
    };

    const normalizeFormData = (data) => ({
        ...data,
        userName: (data.userName ?? '').trim(),
        firstName: (data.firstName ?? '').trim(),
        lastName: (data.lastName ?? '').trim(),
    });

       
    const validateForm = (data) => {
        const normalizedData = normalizeFormData(data);
        const nextFieldErrors = {};
       
        if (!normalizedData.userName?.trim()) {
            nextFieldErrors.userName = 'UserName is required';
        } else if (!/^[a-zA-Z0-9._-]{3,50}$/.test(normalizedData.userName) || containsXssPayload(normalizedData.userName)) {
            nextFieldErrors.userName = 'UserName must be 3-50 characters and contain only letters, numbers, ., _, or -';
        }

        if (!normalizedData.firstName.length) {
            nextFieldErrors.firstName = 'First Name is required';
        } else if (!/^[a-zA-Z\-\s']{1,50}$/.test(normalizedData.firstName) || containsXssPayload(normalizedData.firstName)) {
            nextFieldErrors.firstName = "First Name can only include letters, spaces, apostrophes, and hyphens";
        }

        if (!normalizedData.lastName.length) {
            nextFieldErrors.lastName = 'Last Name is required';
        } else if (!/^[a-zA-Z\-\s']{1,50}$/.test(normalizedData.lastName) || containsXssPayload(normalizedData.lastName)) {
            nextFieldErrors.lastName = "Last Name can only include letters, spaces, apostrophes, and hyphens";
        }

        if (!allowedRoles.includes(normalizedData.role)) {
            nextFieldErrors.role = 'Role is invalid';
        }

        const updatedUser = { ...normalizedData, id: admin.id ?? '' };
        if ((!isEditMode || !isOwnUserName(updatedUser)) && userNameExists(normalizedData.userName)) {
            nextFieldErrors.userName = 'UserName already exists';
            setError('Users can only have one record');
        } else {
            setError('');
        }

        setFieldErrors(nextFieldErrors);
        return { isValid: Object.keys(nextFieldErrors).length === 0, normalizedData };
    };

    const handleSubmit = async (e) => {

        e.preventDefault();
       
        const { isValid, normalizedData } = validateForm(formData);
        if (!isValid) {
            return;
        }

        setIsLoading(true);
        try {
            const payload = isEditMode ? { ...normalizedData, id: admin.id ?? '' } : normalizedData;
            const result = isEditMode
                ? await api.updateDashboardAdmin(payload)
                : await api.createDashboardAdmin(payload);

            if (result) {
                if (result.success) {
                    setSuccessMessage(isEditMode ? 'Admin updated successfully!' : 'Admin added successfully!');
                    fetchAdmins();
                    setTimeout(() => {
                        onClose();
                    }, 2000);
                } else {
                    setError(result.message);
                }
            }
        } catch (error) {
            console.error('Error adding new Admin:', error.message);
            setError('Error adding user')
        } finally {
            setIsLoading(false);
        }
    }

    const clearForm = () => {
        setFormData({
            userName: '',
            firstName: '',
            lastName: '',
            role: 'Admin',
            isActive: true,
        });
        onClose()
    }

    return (
        <div className="modal-overlay">
            <div className="modal">
                <LoadingPanel isLoading={isLoading} />
                <h2>{isEditMode ? 'Edit' : 'Create New'} User</h2>
                {formData && <Form
                    handleSubmit={handleSubmit}
                    handleCancel={(e) => {
                        e.preventDefault()
                        clearForm()
                    }}
                    disabled={false}
                    setFieldErrors={setFieldErrors}
                    SaveButtonText={isEditMode ? 'Save' : 'Add'}
                >
                    <div className="set-form-element">
                        <label htmlFor="userName">Username (Required)</label>
                        {/*NOTE: Passed in "fieldErrors" and "label" because input is required and needs validation*/}
                        <TextInput name="userName" maxLength="50" value={formData.userName} setFieldValue={setFormData} label="Username" fieldErrors={fieldErrors}   />
                    </div>
                    <div className="set-form-element">
                        <label htmlFor="firstName">First Name (Required)</label>
                        {/*NOTE: Passed in "fieldErrors" and "label" because input is required and needs validation*/}
                        <TextInput name="firstName" maxLength="50" value={formData.firstName} setFieldValue={setFormData} label="First Name" fieldErrors={fieldErrors}  />
                    </div>
                    <div className="set-form-element">
                        <label htmlFor="lastName">Last Name (Required)</label>
                        {/*NOTE: Passed in "fieldErrors" and "label" because input is required and needs validation*/}
                        <TextInput name="lastName" maxLength="50" value={formData.lastName} setFieldValue={setFormData} label="Last Name" fieldErrors={fieldErrors}  />
                    </div>
                    <div className='add-admin-double-row'>
                        <div className="set-form-element select-role">
                            <label htmlFor="role">Role</label>
                            {/*NOTE: Passed in "fieldErrors" and "label" because input is required and needs validation*/}
                            <SelectInput name="role" value={formData.role} setFieldValue={setFormData} label="Role" fieldErrors={fieldErrors} >
                                <option key={0} value='Admin'>Admin</option>
                                <option key={1} value='SuperAdmin'>SuperAdmin</option>
                            </SelectInput>
                        </div>
                        <div className="set-form-element">
                            {/*NOTE: Passed in "fieldErrors" and "label" because input is required and needs validation*/}
                            <label htmlFor="isActive">Active</label>
                            <CheckboxInput name="isActive" checked={formData.isActive} setFieldValue={setFormData} />
                        </div>
                    </div>
                </Form>}
                <div className='error-container'>
                    {error}
                </div>
                <div className='success-container'>
                    {successMessage && <span className="success-message">{successMessage}</span>}
                </div>
            </div>
        </div>
    );
};

export default AdminModal;